import React, { lazy, Suspense } from 'react';
import { useAuth } from '../components/AuthContext';  
import { useTranslation, Trans } from 'react-i18next';
import { Typography } from "@material-tailwind/react";
import { Link } from 'react-router-dom'; 
import { reverse } from '../pages/urls'; // Import the reverse function

// Define the SOCIAL_ICONS array with icon names as strings
// Importing icons from Material-UI
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FavoriteIcon from '@mui/icons-material/Favorite';

// Static mapping of icon names to their respective components
const Icons = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  LinkedIn: LinkedInIcon,
  X: XIcon,
  YouTube: YouTubeIcon,
};

const SOCIAL_ICONS = [
  { url: "https://www.facebook.com/profile.php?id=61569366455921", icon: 'Facebook' },
  { url: "https://www.instagram.com/thatshirtshow/", icon: 'Instagram' },
  // { url: "https://youtube.com/@ThatShirtShow", icon: 'YouTube' },
  // { url: "https://linkedin.com/company/thatshirtshow", icon: 'LinkedIn' },
  // { url: "https://x.com/ThatShirtShow/", icon: 'X' },
];


const HTMLRenderer = ({ htmlContent, isBuiltBy = false }) => {
  if (isBuiltBy) {
    return (
      <span dangerouslySetInnerHTML={{
        __html: htmlContent.replace(
          /<0>(.*?)<\/0>/,
          '<a href="https://www.paulhoft.com" target="_blank" rel="noopener noreferrer"><b>$1</b></a>'
        )
      }} />
    );
  }
  return (
    <span dangerouslySetInnerHTML={{
      __html: htmlContent.replace(/<0>/g, '<b>').replace(/<\/0>/g, '</b>')
    }} />
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const { isAuthorized } = useAuth();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  // Helper function to convert \n to <br />
  const formatText = (text) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index !== text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const SITEMAP = [
    {
      title: t('footer.orderInformation.title'),
      links: [
        { name: "How to Order", url: "/how-to-order" },
        { name: "Payment Methods", url: "/payment-methods" },
        { name: "Return Policy", url: "/return-policy" },
        { name: "Privacy Policy", url: "/privacy-policy" },
        { name: "Disclaimer", url: "/disclaimer" },
      ],
    },
    {
      title: "Store Hours",
      content: (
        <div className="space-y-2">
          <Typography
            variant="small"
            color="blue-gray"
            className="font-normal text-white/90 text-sm"
          >
            <b>Monday - Friday</b>:<br/>9:00 AM - 6:00 PM EST <br/><br/>
            <b>Saturday:</b><br/> 10:00 AM - 4:00 PM EST <br/>
            <br/>
            <b>Sunday:</b><br/>Closed 
          </Typography>
        </div>
      ),
    },
    {
      title: t('footer.contactUs.title'),
      links: [
        { name: t('footer.contactUs.contactLink'), reverse: "contact" },
        { name: "About Us", url: "/about-us" },
        { name: "+27 (0)76 247 5776", url: "tel:+27762475776" },
        { name: "info@thatshirtshow.com", url: "mailto:info@thatshirtshow.com" },
      ],
    },
    {
      title: t('footer.myAccount.title'),
      links: [
        { name: t('footer.myAccount.login'), reverse: "login", auth: false},
        { name: t('footer.myAccount.updateProfile'), reverse: "profile", auth: true},
        { name: t('footer.myAccount.valueCard'), reverse: "valuecard", auth: true},
        { name: t('footer.myAccount.myBasket'), reverse: "basket"},
        { name: t('footer.myAccount.myOrders'), reverse: "orders", auth: true },
        { name: t('footer.myAccount.forgotPassword'), reverse: "forgot_password", auth: false},
        { name: t('footer.myAccount.register'), reverse: "register", auth: false},
        { name: t('footer.myAccount.myReturns'), reverse: "returns", auth: true },
        { name: t('footer.myAccount.changeEmail'), reverse: "change_email", auth: true},
        { name: t('footer.myAccount.changePassword'), reverse: "change_password", auth: true},
        { name: t('footer.myAccount.logout'), reverse: "logout", auth: true},
      ],
    },
    {
      title: t('footer.helpSupport.title'),
      links: [
        { name: t('footer.helpSupport.faq'), url: "/support/faq" },
        { name: t('footer.helpSupport.makeReturn'), url: "/support/make-a-return" },
      ],
    },
    {
      title: "", // Empty block for spacing
      content: <div></div>,
    },
    {
      title: "", // Empty block for spacing
      content: <div></div>,
    },
    {
      title: "Follow Us",
      content: (
        <div className="flex gap-4 text-white justify-center md:justify-start">
          {SOCIAL_ICONS.map(({ url, icon }, key) => {
            const IconComponent = Icons[icon];
            return (
              <Suspense fallback={<div>Loading...</div>} key={key}>
                <Typography 
                  as="a" 
                  href={url} 
                  target="_blank" 
                  className="opacity-80 transition-opacity hover:opacity-100"
                  onClick={scrollToTop}
                >
                  <IconComponent className="text-white text-xl" />
                </Typography>
              </Suspense>
            );
          })}
        </div>
      ),
    },
  ];

  const renderHTML = (htmlString) => {
    return { __html: htmlString };
  };

  return (
    <>
      <div className="w-full bg-[#111] py-8 border-t border-white/10">
        <div className="merch-container">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <img src="/img/logo-inverted.png" alt="That Shirt Show" className="w-[190px] h-auto" />
            <p className="merch-eyebrow max-w-xl">
              Bespoke custom tees and hoodies<br />
              built for creators, events and brands
            </p>
            <img 
              src="/img/payment-gateways2.png" 
              alt="Payment Methods Accepted" 
              className="max-w-full h-auto opacity-90"
              style={{ maxHeight: '70px' }}
            />
          </div>
        </div>
      </div>
      <footer className="relative w-full bg-[#0b0b0b] border-t border-white/10">
        <div className="merch-container">
          <div className="mx-auto grid w-full grid-cols-1 gap-8 py-12 md:grid-cols-2 lg:grid-cols-4">
            {SITEMAP.map(({ title, links, content }, key) => (
              <div key={key} className="w-full text-center md:text-left">
                <Typography
                  variant="small"
                  className="mb-4 font-bold uppercase text-white merch-font"
                >
                  {title}
                </Typography>
                {content ? (
                  <div className="text-sm text-white/75">
                    {content}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {links?.map(({ name, target, reverse: reverseName, url, auth }, linkKey) => {
                      if (auth !== undefined && auth !== isAuthorized) return null;
                      const linkUrl = reverseName ? reverse(reverseName) : url;
                      return (
                        <Typography key={linkKey} as="li" className="font-normal">
                          <Link 
                            to={linkUrl} 
                            target={target} 
                            className="text-sm text-white/65 hover:text-[#aade00] transition-colors duration-200"
                            onClick={scrollToTop}
                          >
                            {name}
                          </Link>
                        </Typography>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <div className="flex w-full flex-col items-center justify-center border-t border-white/10 py-5">
            <Typography
              variant="small"
              className="text-center font-normal text-white/55"
            >
              &copy; {currentYear} ThatShirtShow. All Rights Reserved
            </Typography>
            <a
              href="https://tensology.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs normal-case tracking-wide text-white/45 transition-colors hover:text-[#aade00]"
            >
              <span>Built with </span>
              <FavoriteIcon sx={{ fontSize: 14 }} aria-label="love" />
              <span> by tensology.com</span>
            </a>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
